import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { endpoints } from '@/API/endpoints';
import Button from '@/components/ui/button/Button';
import PlusFill from '@/components/ui/icons/PlusFill';
import Redact from '@/components/ui/icons/Redact';
import { UserRoleContext } from '@/context/RoleContext';
import SelectWrapper from '@/forms/components/simpleWidgets/select-input/SelectWrapper';
import { Choice } from '@/forms/dto';
import { createParams } from '@/helpers/createParams';
import { useApi } from '@/hooks/useApi/useApi';

import CalendarDatePicker from '../calendar-date-picker/CalendarDatePicker';
import classes from './Filters.module.scss';

interface TFiltersProps {
	isSupervisorEditUserEmployment: boolean;
	setIsSupervisorEditUserEmployment: Dispatch<SetStateAction<boolean>>;
}

const Filters = ({
	isSupervisorEditUserEmployment,
	setIsSupervisorEditUserEmployment,
}: TFiltersProps) => {
	const { userRoleContext } = useContext(UserRoleContext);
	const { apiGet } = useApi();
	const [searchParams, setSearchParams] = useSearchParams();
	const [projectsList, setProjectsList] = useState<Choice[] | null>(null);
	const [membersList, setMembersList] = useState<Choice[] | null>(null);
	const [positionList, setPositionList] = useState<Choice[] | null>(null);

	const onChangeDepartment = (value: unknown) => {
		if (value) {
			setSearchParams(createParams(searchParams, { department: value.toString() }));
		}
	};

	const onChangePosition = (value: unknown) => {
		if (value) {
			setSearchParams(createParams(searchParams, { position: value.toString() }));
		}
	};

	const onChangeProject = (value: unknown) => {
		if (value) {
			setSearchParams(createParams(searchParams, { project: value.toString() }));
		}
	};

	const onChangeEmployee = (value: unknown) => {
		if (value) {
			setSearchParams(
				createParams(searchParams, {
					employee: value.toString(),
				})
			);
		}
	};

	useEffect(() => {
		const getProjectsList = async () => {
			const res = await apiGet(endpoints.planningChoices + 'project/');
			setProjectsList(res.data);
		};

		const getMembersList = async () => {
			const res = await apiGet(endpoints.planningChoices + 'employee/');
			setMembersList(res.data);
		};

		const getPositionList = async () => {
			const res = await apiGet(endpoints.planningChoices + 'position/');
			setPositionList(res.data);
		};

		getMembersList();
		getProjectsList();
		getPositionList();
	}, []);

	const optionStyle = { fontSize: '1rem', fontWeight: 500, lineHeight: '20px' };

	return (
		<div className={[classes.container, 'planning-filters'].join(' ')}>
			<div className={classes.selects}>
				{userRoleContext.data?.can_view_by_department && (
					<SelectWrapper
						name='departments'
						placeholder='Подразделение'
						value={searchParams.get('department')}
						choices={[
							{ name: 'Все подразделения', label: 'all', value: 'all' },
							{ name: 'Белгород', label: 'bel', value: '2' },
						]}
						onChange={onChangeDepartment}
						searchable={true}
						optionStyle={optionStyle}
						theme='outline'
					/>
				)}
				{positionList && (
					<SelectWrapper
						name='positions'
						placeholder='Должность'
						value={Number(searchParams.get('position'))}
						choices={[
							{ name: 'Все должности', label: 'all', value: 'all' },
							...positionList,
						]}
						onChange={onChangePosition}
						searchable={true}
						optionStyle={optionStyle}
						theme='outline'
					/>
				)}
				{projectsList && (
					<SelectWrapper
						name='projects'
						placeholder='Проекты'
						value={Number(searchParams.get('project'))}
						choices={[
							{ name: 'Все проекты', label: 'all', value: 'all' },
							...projectsList,
						]}
						onChange={onChangeProject}
						searchable={true}
						optionStyle={optionStyle}
						theme='outline'
					/>
				)}
				{membersList && (
					<SelectWrapper
						name='employees'
						placeholder='Сотрудники'
						value={Number(searchParams.get('employee'))}
						choices={[
							{ name: 'Все сотрудники', label: 'all', value: 'all' },
							...membersList,
						]}
						onChange={onChangeEmployee}
						searchable={true}
						optionStyle={optionStyle}
						theme='outline'
					/>
				)}
				{isSupervisorEditUserEmployment && (
					<CalendarDatePicker buttonClass={classes.calendar} />
				)}
			</div>
			<div className={classes.buttons}>
				{isSupervisorEditUserEmployment ? (
					<Button
						clazz={classes.button}
						onClick={() => setIsSupervisorEditUserEmployment(false)}
						success
					>
						Применить изменения
					</Button>
				) : (
					<>
						<Button
							icon={<Redact />}
							clazz={classes.button}
							onClick={() => setIsSupervisorEditUserEmployment(true)}
						>
							Внести изменения
						</Button>
						<Button icon={<PlusFill />} success clazz={classes.buttonPlus}>
							Добавить проект
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default Filters;
